import JsBarcode from 'jsbarcode'
import logger from '../utils/logger'

// Use exact dimensions from card editor
const CARD_WIDTH = 700
const CARD_HEIGHT = 350

export const generateCardHTML = (card, isTemplate = false) => {
  // Updated container styles to match exact dimensions

  logger.log('Generating card HTML with card data:', card)

  const cardStyles = {
    width: '700px',
    height: '350px',
    position: 'relative',
    borderRadius: '10px',
    overflow: 'hidden',
    backgroundColor: '#ffffff', // Ensure white background
  }

  // Corrected image URL helper
  const getImageUrl = (path) => {
    if (!path) return ''
    if (isTemplate) return path
    // Remove any double slashes except after protocol
    return `${import.meta.env.VITE_BASE_URL}${path}`.replace(/([^:])\/+/g, '$1/')
  }

  // Updated getStampsHTML to match exactly with creation styles
  const getStampsHTML = () => {
    if (card.rewardStyle !== 'stamps') return ''

    const { stampContainerStyles, stampStyles } = card
    const stamps = card.stamps || []
    const { stampsPerRow, width: containerWidth, gap } = stampContainerStyles

    // Calculate stamp size based on container width, gaps, and stamps per row
    const totalGapsWidth = (stampsPerRow - 1) * gap
    const stampSize = Math.floor((containerWidth - totalGapsWidth) / stampsPerRow)

    return `
      <div style="
        position: absolute;
        left: ${stampContainerStyles.x}px;
        top: ${stampContainerStyles.y}px;
        width: ${containerWidth}px;
        display: grid;
        grid-template-columns: repeat(${stampsPerRow}, ${stampSize}px);
        gap: ${gap}px;
        justify-content: center;
      ">
      ${stamps
        .map((stamp) => {
          const style = stampStyles[stamp.type]
          return `
          <div style="
            width: ${stampSize}px;
            height: ${stampSize}px;
            border: ${style?.borderWidth}px solid ${style?.borderColor};
            border-radius: 50%;
            background-color: ${style.backgroundColor};
            opacity: ${stamp.type === 'unfilled' ? 0.7 : 1};
            position: relative;
            ${
              stamp.type === 'filled' && style.image
                ? `
              background-image: url('${getImageUrl(style.image)}');
              background-size: contain;
              background-position: center;
              background-repeat: no-repeat;
            `
                : ''
            }
            ${
              stamp.type === 'free'
                ? `
              display: flex;
              align-items: center;
              justify-content: center;
            `
                : ''
            }
          ">
            ${
              stamp.type === 'free'
                ? `
              <span style="
                font-family: ${style.fontFamily};
                font-size: ${style.fontSize}px;
                color: ${style.color};
                font-weight: ${style.fontStyle};
                line-height: 1;
                text-align: center;
                user-select: none;
              ">${style.text}</span>
            `
                : ''
            }
          </div>
        `
        })
        .join('')}
      </div>
    `
  }

  // Scale factor to maintain element positions relative to original 700x350 design
  const scaleX = CARD_WIDTH / 700
  const scaleY = CARD_HEIGHT / 350

  // Helper to scale positions and sizes
  const scaleValue = (value, isX = true) => Math.round(value * (isX ? scaleX : scaleY))

  // Simplified text styles matching API response exactly
  const getTextStyles = (element) => `
    position: absolute;
    left: ${element.x}px;
    top: ${element.y}px;
    font-family: ${element.fontFamily};
    font-size: ${element.fontSize}px;
    color: ${element.color};
    font-style: ${element.fontStyle.includes('italic') ? 'italic' : 'normal'};
    font-weight: ${element.fontStyle.includes('bold') ? 'bold' : 'normal'};
    white-space: pre-wrap;
    line-height: 1;
vertical-align: middle; 
  `

  // Clean text element creation
  const createTextElement = (element) => `
    <div style="${getTextStyles(element)}">${element.text}</div>
  `

  // Updated barcode generator to match editor settings
  const generateBarcodeDataURL = (settings) => {
    const canvas = document.createElement('canvas')
    canvas.width = settings.width
    canvas.height = settings.height

    // Use full height for bars
    const barHeight = settings.height
    const barWidth = Math.max(1, Math.floor(settings.width / 50))

    JsBarcode(canvas, card.textElements.customerId.text || '1234567890', {
      format: 'CODE128',
      width: barWidth,
      height: barHeight,
      displayValue: false,
      lineColor: settings.color,
      background: settings.bgColor,
      margin: 0,
      padding: 0,
      textMargin: 0,
    })

    return canvas.toDataURL('image/png')
  }

  // Updated barcode container
  const getBarcodeHTML = () => {
    const barcodeDataURL = generateBarcodeDataURL(card.barcodeSettings)
    return `
      <div style="
        position: absolute;
        left: ${card.barcodeSettings.x}px;
        top: ${card.barcodeSettings.y}px;
        width: ${card.barcodeSettings.width}px;
        height: ${card.barcodeSettings.height}px;
        background: ${card.barcodeSettings.bgColor};
        border-radius: 4px;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        padding: 0;
      ">
        <img 
          src="${barcodeDataURL}" 
          style="
            width: 100%;
            height: 100%;
            object-fit: fill;
            display: block;
          "
        />
      </div>
    `
  }

  // Generate reward display based on style
  const getRewardDisplayHTML = () => {
    if (card.rewardStyle === 'stamps') {
      return getStampsHTML()
    }

    if (card.rewardStyle === 'progress') {
      const { progressBarStyles } = card
      return `
        <div style="
          position: absolute;
          left: ${scaleValue(progressBarStyles.x)}px;
          top: ${scaleValue(progressBarStyles.y, false)}px;
          width: ${scaleValue(progressBarStyles.width)}px;
          height: ${scaleValue(progressBarStyles.height, false)}px;
          background-color: ${progressBarStyles.backgroundColor};
          border-radius: ${scaleValue(progressBarStyles.borderRadius)}px;
          border: ${scaleValue(progressBarStyles.borderWidth)}px solid ${
            progressBarStyles.borderColor
          };
          overflow: hidden;
        ">
          <div style="
            width: ${progressBarStyles.progress}%;
            height: 100%;
            background-color: ${progressBarStyles.fillColor};
          "></div>
        </div>
      `
    }

    return ''
  }

  return `
    <div style="
      ${Object.entries(cardStyles)
        .map(([key, value]) => `${key}: ${value}`)
        .join(';')};
      background: ${
        card.background.type === 'color'
          ? card.background.value
          : `url('${getImageUrl(card.background.value)}')`
      };
      background-size: cover;
      background-position: center;
      transform-origin: top left;
      border-radius:10px;
    ">
      <!-- Logo with absolute URL -->
        ${
          card?.logoSettings?.image
            ? `
      <img 
        src="${getImageUrl(card.logoSettings.image)}"
        style="
          position: absolute;
          left: ${card.logoSettings.x}px;
          top: ${card.logoSettings.y}px;
          width: ${card.logoSettings.width}px;
          height: ${card.logoSettings.height}px;
          border-radius: ${card.logoSettings.borderRadius}%;
          object-fit: cover;
          transform-origin: top left;
        "
      />
            `
            : ''
        }

            <!-- Stampin Logo -->
      ${
        card?.stampinLogoSettings?.visible
          ? `
        <img 
          src="${card?.stampinLogoSettings?.defaultImage}"
          style="
            position: absolute;
            left: ${card?.stampinLogoSettings?.x}px;
            top: ${card?.stampinLogoSettings?.y}px;
            width: ${card?.stampinLogoSettings?.width}px;
            height: ${card?.stampinLogoSettings?.height}px;
            object-fit: contain;
            transform-origin: top left;
          "
        />
      `
          : ''
      }

      <!-- Text Elements using span instead of div -->
      ${createTextElement(card.textElements.businessName)}
      ${createTextElement(card.textElements.tagline)}
      ${createTextElement(card.textElements.contactDetails)}
      ${createTextElement(card.textElements.offerText)}
      ${createTextElement(card.textElements.progressText)}
      ${createTextElement(card.textElements.customerId)}


      <!-- Reward Elements -->
      ${getRewardDisplayHTML()}
      ${getBarcodeHTML()}
    </div>
  `
}
